import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { getAllConversations, fetchAdminMessages, sendAdminMessage, startAdminConversation, getUsers } from '../api/chatApi';
import { getSocket } from '../support/socket';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from '../components/ui';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';

export default function AdminSupport() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const bottomRef = useRef(null);

  const [showNewConv, setShowNewConv] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  const socket = useMemo(() => getSocket(), []);

  const loadInbox = useCallback(async () => {
    try {
      const data = await getAllConversations();
      setConversations(data || []);
    } catch (e) {
      console.error("Erreur chargement inbox", e);
    }
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  const handleSelectConv = async (conv) => {
    setSelectedConv(conv);
    setMessages([]);
    setShowNewConv(false);
    try {
      const msgs = await fetchAdminMessages(conv._id);
      setMessages(msgs);
      socket.emit("conversation:join", { conversationId: conv._id });
      scrollToBottom();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const onNewMessage = (payload) => {
      const msg = payload?.message;
      if (!msg) return;

      if (selectedConv && msg.conversationId === selectedConv._id) {
        setMessages((prev) => {
          if (prev.some(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        scrollToBottom();
      }

      setConversations(prev => prev.map(c => {
        if (c._id === msg.conversationId) {
          return { ...c, lastMessagePreview: msg.body, lastMessageAt: msg.createdAt };
        }
        return c;
      }).sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)));
    };

    socket.on("message:new", onNewMessage);
    return () => socket.off("message:new", onNewMessage);
  }, [socket, selectedConv]);

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedConv) return;
    try {
      await sendAdminMessage(selectedConv._id, replyText);
      setReplyText("");
    } catch (e) {
      console.error("Erreur envoi", e);
      alert("Erreur lors de l'envoi du message");
    }
  };

  const handleOpenNewConv = async () => {
    setShowNewConv(true);
    setSelectedConv(null);
    setLoadingUsers(true);
    try {
      const data = await getUsers();
      setUsers(data || []);
    } catch (e) {
      console.error("Erreur chargement utilisateurs", e);
      toastError("Impossible de charger la liste des utilisateurs");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleStartConv = async (user) => {
    try {
      const conv = await startAdminConversation(user._id);
      await loadInbox();
      setShowNewConv(false);
      handleSelectConv(conv);
      toastSuccess(`Conversation démarrée avec ${user.name}`);
    } catch (e) {
      console.error("Erreur création conversation", e);
      toastError("Impossible de démarrer la conversation");
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const filteredUsers = users.filter(u =>
    !searchTerm || u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.tenantName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-100px)] bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200 mt-4 mx-4">

      {/* GAUCHE : LISTE DES CONVS */}
      <div className="w-1/3 border-r border-gray-100 bg-gray-50 flex flex-col">
        <div className="p-4 bg-white border-b border-gray-100 flex items-center justify-between shadow-sm z-10">
          <span className="font-bold text-gray-800">
            {showNewConv ? "Nouvelle conversation" : "Boîte de réception"}
          </span>
          {!showNewConv && (
            <button
              onClick={handleOpenNewConv}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <Icon name="IconPlus" size={16} />
              Nouveau
            </button>
          )}
          {showNewConv && (
            <button
              onClick={() => setShowNewConv(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Retour
            </button>
          )}
        </div>

        {showNewConv ? (
          <div className="flex-1 overflow-y-auto">
            <div className="p-3 border-b border-gray-100">
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {loadingUsers ? (
              <div className="p-4 text-center text-gray-400">Chargement...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-400">Aucun utilisateur trouvé</div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user._id}
                  onClick={() => handleStartConv(user)}
                  className="p-4 border-b border-gray-100 cursor-pointer hover:bg-white transition-all"
                >
                  <div className="font-semibold text-gray-800">{user.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{user.email}</div>
                  {user.tenantName && (
                    <div className="text-xs text-gray-400 mt-0.5">{user.tenantName}</div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="p-4 text-center text-gray-400">Aucune conversation</div>
            )}
            {conversations.map(conv => (
              <div
                key={conv._id}
                onClick={() => handleSelectConv(conv)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-white transition-all ${
                  selectedConv?._id === conv._id ? 'bg-white border-l-4 border-blue-600 shadow-sm' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <div className="font-semibold text-gray-800 truncate w-32" title={conv.customerName || conv.customerUserId}>
                    {conv.customerName || `Client #${conv.customerUserId?.slice(-4)}`}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString()}
                  </div>
                </div>
                {conv.tenantName && (
                  <div className="text-xs text-blue-600 mb-0.5">{conv.tenantName}</div>
                )}
                <div className="text-xs text-gray-500 truncate h-4">
                  {conv.lastMessagePreview || "Nouvelle conversation"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DROITE : CHAT */}
      <div className="w-2/3 flex flex-col bg-white">
        {selectedConv ? (
          <>
            <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center shadow-sm z-10">
              <div>
                <h3 className="font-bold text-gray-800">
                  {selectedConv.customerName || `Client #${selectedConv.customerUserId?.slice(-4)}`}
                </h3>
                {selectedConv.tenantName && (
                  <p className="text-xs text-blue-600">{selectedConv.tenantName}</p>
                )}
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span> En ligne
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
              {messages.map((m) => {
                const isMe = m.senderType === 'agent';
                return (
                  <div key={m._id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm shadow-sm ${
                      isMe
                        ? 'bg-blue-600 text-white rounded-br-none'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                    }`}>
                      <div>{m.body}</div>
                      <div className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                        {formatTime(m.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={handleSend} className="p-4 bg-white border-t border-gray-100">
              <div className="flex gap-2 bg-gray-50 p-1.5 rounded-full border border-gray-200 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <input
                  className="flex-1 bg-transparent px-4 py-2 focus:outline-none text-sm text-gray-700 placeholder-gray-400"
                  placeholder="Écrivez votre réponse..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={!replyText.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors shadow-sm"
                >
                  <Icon name="IconSend" size={14} />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-300 bg-gray-50/30">
            <Icon name="IconInbox" size={48} className="mb-4 text-gray-200" />
            <p>Sélectionnez une conversation pour traiter le ticket.</p>
          </div>
        )}
      </div>
    </div>
  );
}
